/**
 * Casca de `/revisao`: herói, fichas da seleção, trilho das cinco dimensões
 * e as seções numeradas que o pai entrega em `children`. A barra é
 * `BarraDeInicio` e lê o mesmo `leitura.total` do herói. Cada ficha remove
 * um valor guardado por vez.
 */
import type { ReactNode } from "react";
import {
  BookOpen,
  CalendarDays,
  Layers,
  SlidersHorizontal,
  Tags,
  type LucideIcon,
} from "lucide-react";
import BarraDeInicio from "../BarraDeInicio";
import { DIA_FICHA_DIMENSION, type FichaDeFiltro } from "../barraDeInicioContrato";
import RevisaoActiveFilters, {
  type RevisaoActiveFilterToken,
} from "../filtros/RevisaoActiveFilters";
import RevisaoFilterRail, {
  type RevisaoFilterRailEntry,
} from "../filtros/RevisaoFilterRail";
import RevisaoHero from "./RevisaoHero";
import {
  REVISAO_RAIL,
  idSecao,
  trilhoPorId,
  type RevisaoDiaLeitura,
  type RevisaoRailId,
  type RevisaoTrilhoContagens,
} from "./revisaoHome";

export interface RevisaoHomeShellProps {
  leitura: RevisaoDiaLeitura;
  contagens: RevisaoTrilhoContagens;
  activeId: RevisaoRailId;
  onSelect: (id: RevisaoRailId) => void;
  onIniciar: () => void;
  /** A primeira leitura ainda não chegou. A barra não trata o zero como vazio. */
  aguardando?: boolean;
  fichas: FichaDeFiltro[];
  onRemoverFicha: (ficha: FichaDeFiltro) => void;
  onLimpar: () => void;
  podeLimpar: boolean;
  ahead: boolean;
  onAhead: (ahead: boolean) => void;
  children: ReactNode;
}

const ICONE_DO_TRILHO: Record<RevisaoRailId, LucideIcon> = {
  agenda: CalendarDays,
  materia: BookOpen,
  palavras: Tags,
  baralhos: Layers,
  modo: SlidersHorizontal,
};

function entradasDoTrilho(contagens: RevisaoTrilhoContagens): RevisaoFilterRailEntry[] {
  return REVISAO_RAIL.map((entrada) => {
    const Icone = ICONE_DO_TRILHO[entrada.id];
    const contagem = contagens[entrada.id];
    return {
      id: entrada.id,
      label: entrada.label,
      icon: <Icone aria-hidden="true" />,
      selected: contagem.selected,
      total: contagem.total,
    };
  });
}

function comoFichaDeFiltro(item: RevisaoActiveFilterToken): FichaDeFiltro | null {
  if (
    item.dimension !== "subjects" &&
    item.dimension !== "keywords" &&
    item.dimension !== "decks" &&
    item.dimension !== DIA_FICHA_DIMENSION
  ) {
    return null;
  }
  return {
    dimension: item.dimension,
    dimensionLabel: item.dimensionLabel,
    value: item.value,
    label: item.label && item.label !== "" ? item.label : item.value,
  };
}

function irParaSecao(id: string, onSelect: (railId: RevisaoRailId) => void): void {
  const entrada = trilhoPorId(id);
  if (!entrada) return;
  onSelect(entrada.id);
  if (typeof document === "undefined") return;
  document.getElementById(idSecao(entrada.id))?.scrollIntoView({ block: "start" });
}

export default function RevisaoHomeShell({
  leitura,
  contagens,
  activeId,
  onSelect,
  onIniciar,
  aguardando = false,
  fichas,
  onRemoverFicha,
  onLimpar,
  podeLimpar,
  ahead,
  onAhead,
  children,
}: RevisaoHomeShellProps): JSX.Element {
  return (
    <div className="mx-auto flex min-h-[calc(100dvh-7.5rem)] w-full max-w-6xl flex-col gap-5 px-4 py-6">
      <RevisaoHero leitura={leitura} aguardando={aguardando} />
      <RevisaoActiveFilters
        items={fichas}
        onRemove={(item) => {
          const ficha = comoFichaDeFiltro(item);
          if (ficha) onRemoverFicha(ficha);
        }}
        onClearAll={onLimpar}
      />
      <div className="flex min-w-0 flex-col gap-5 md:flex-row md:items-start">
        <aside className="rounded-card border border-edge bg-surface p-3 md:w-72 md:shrink-0">
          <RevisaoFilterRail
            entries={entradasDoTrilho(contagens)}
            activeId={activeId}
            onSelect={(id) => irParaSecao(id, onSelect)}
          />
        </aside>
        <div className="min-w-0 flex-1">{children}</div>
      </div>
      <BarraDeInicio
        cartoes={leitura.total}
        aguardando={aguardando}
        ahead={ahead}
        onAhead={onAhead}
        onLimpar={onLimpar}
        podeLimpar={podeLimpar}
        onIniciar={onIniciar}
      />
    </div>
  );
}
